'use client';

import React, { useEffect, useState } from 'react';
import { type ProductListItem } from '@/common/types/product.type';
import { useGetStoreProductList } from '@/common/apis/storefronts/v1/get-product-list.api';
import EmptyState from '@/common/components/empty-state';
import Skeleton from '@/common/components/skeleton';
import createUrlUtility from '@/common/utils/create-url.utility';
import Product from '@/features/store/components/product.component';

interface TenantSearchResultsProps {
  searchTerm: string;
}

export default function TenantSearchResults({
  searchTerm,
}: TenantSearchResultsProps) {
  const trimmed = searchTerm.trim();
  const hasSearchTerm = trimmed.length > 0;
  const [products, setProducts] = useState<ProductListItem[]>([]);

  const { data, isFetching, isSuccess, isLoading, isError } =
    useGetStoreProductList({
      enabled: hasSearchTerm,
      params: {
        searchByProductName: trimmed,
        sortDirection: undefined,
        sortCondition: undefined,
        categoryList: [],
        isAvailable: false,
        isDiscounted: false,
        pageSize: 20,
        pageIndex: 1,
        vendorSlug: '',
      },
    });

  useEffect(() => {
    if (!hasSearchTerm) {
      setProducts([]);
      return;
    }
    if (isSuccess && !isFetching) {
      setProducts(data?.data?.items ?? []);
    }
  }, [hasSearchTerm, isSuccess, isFetching, data]);

  if (!hasSearchTerm) {
    return null;
  }

  if (isLoading || isFetching) {
    return (
      <div className="mt-5 w-full">
        <h2 className="mb-7 w-full text-right text-sm font-semibold text-black-400 lg:text-xl">
          نتیجه جست‌وجو
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 pb-8 lg:flex lg:flex-wrap lg:justify-between">
          <Skeleton type="product-item" repeat={10} />
        </div>
      </div>
    );
  }

  if (isError || products.length === 0) {
    return (
      <div className="mt-5 w-full">
        <h2 className="mb-7 w-full text-right text-sm font-semibold text-black-400 lg:text-xl">
          نتیجه جست‌وجو
        </h2>
        <EmptyState
          iconSrc={createUrlUtility({
            params: 'static/images/svg/empty-state-product.svg',
          })}
          className="!static my-4"
          message="نتیجه ای یافت نشد!"
        />
      </div>
    );
  }

  return (
    <div className="mt-5 w-full">
      <h2 className="mb-7 w-full text-right text-sm font-semibold text-black-400 lg:text-xl">
        نتیجه جست‌وجو
      </h2>
      <div className="relative grid w-full grid-cols-[repeat(2,1fr)] justify-between gap-x-[16px] pb-8 md:grid-cols-[repeat(5,212px)] md:gap-x-[27px] lg:gap-y-10">
        {products.map((item) => (
          <div
            className="box-border h-full w-full cursor-pointer border-none"
            key={item.id}
          >
            <Product itemDetail={item} vendorSlug="" />
          </div>
        ))}
      </div>
    </div>
  );
}
